import numeral from 'numeral';

/**
 * Formats net worth values for display in the enrichment tab.
 * All values are formatted using numeral.js (e.g., $2.40m, $956.25k).
 */
export const formatNetWorths = (netWorths) => {
  return netWorths.map(({ id, rejected, updated_at, net_worth }) => {
    return {
      value: numeral(net_worth).format('$0.00a'),
      id,
      rejected,
      updated_at,
    };
  });
};

/**
 * Formats a currency value for display.
 * Returns null for zero/null/undefined values.
 */
export const formatCurrency = (value) => {
  if (value === null || value === undefined || value === 0) return null;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const CAPACITY_RANGES = [
  [0, 24999, '<$25K'],
  [25000, 49999, '$25K–$49K'],
  [50000, 99999, '$50K–$99K'],
  [100000, 249999, '$100K–$249K'],
  [250000, 499999, '$250K–$499K'],
  [500000, 999999, '$500K–$999K'],
  [1000000, 2499999, '$1M–$2.4M'],
  [2500000, 4999999, '$2.5M–$4.9M'],
  [5000000, 9999999, '$5M–$9.9M'],
  [10000000, 24999999, '$10M–$24M'],
  [25000000, 49999999, '$25M–$49M'],
  [50000000, 99999999, '$50M–$99M'],
  [100000000, 249999999, '$100M–$249M'],
  [250000000, 499999999, '$250M–$499M'],
  [500000000, 999999999, '$500M–$999M'],
  [1000000000, Infinity, '$1B+'],
];

/**
 * Formats a capacity value into a range label (e.g., "$1M–$2.4M" for 1,500,000).
 * Returns null for null/undefined values.
 */
export const formatCapacityRange = (value) => {
  if (value == null) return null;
  const match = CAPACITY_RANGES.find(([min, max]) => value >= min && value <= max);
  return match ? match[2] : null;
};

/**
 * Formats an ISO date string for display (e.g., "Feb 3, 2026").
 */
export const formatDate = (dateString) => {
  if (!dateString) return null;
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Extracts amounts and high/low quality count breakdowns from the /summary API response.
 * API returns camelCase keys like givingAmountHighQuality, crowdfundingCountHighQuality, etc.
 */
export const transformDonorSearchSummary = (apiResponse = {}) => ({
  charitableGiving: {
    amount: apiResponse.givingAmountHighQuality || null,
    highCount: apiResponse.givingCountHighQuality || 0,
    lowCount: apiResponse.givingCountLowQuality || 0,
  },
  politicalGiving: {
    amount: apiResponse.politicalGivingAmountHighQuality || null,
    highCount: apiResponse.politicalGivingCountHighQuality || 0,
    lowCount: apiResponse.politicalGivingCountLowQuality || 0,
  },
  crowdfunding: {
    amount: apiResponse.crowdfundingAmountHighQuality || null,
    highCount: apiResponse.crowdfundingCountHighQuality || 0,
    lowCount: apiResponse.crowdfundingCountLowQuality || 0,
  },
  realEstate: {
    amount: apiResponse.realEstateAmountHighQuality || null,
    highCount: apiResponse.realEstateCountHighQuality || 0,
    lowCount: apiResponse.realEstateCountLowQuality || 0,
  },
  secHoldings: {
    amount: apiResponse.secAmountHighQuality || null,
    highCount: apiResponse.secCountHighQuality || 0,
    lowCount: apiResponse.secCountLowQuality || 0,
  },
});

/** Rounds a score value for display, returning null for missing values. */
const roundScore = (value) => (value != null ? Math.round(Number(value)) : null);

/** Returns the most recently updated record from a list, or null if empty. */
const mostRecentRecord = (records) =>
  records.reduce((latest, record) => {
    if (!latest) return record;
    return new Date(record.updated_at) > new Date(latest.updated_at) ? record : latest;
  }, null);

/**
 * Selects the primary giving score to display from DB-sourced AI score records,
 * applying the DS package-to-score mapping: DSAi (DSAI) > Enhanced Core (ECR) > Core (MLR) > DPS fallback.
 * Records with a null score_value are ignored. When multiple records share a type,
 * the most recently updated one wins.
 *
 * @param {Array} donorsearchAiScores - Raw donorsearch_ai_scores records from GQL (type, score_value, updated_at)
 * @param {number|null} dpsScore - DPS fallback score (et_score.dds_score)
 * @returns {{ type: 'DSAI'|'ECR'|'MLR'|'DPS', value: number|null }}
 */
export const selectGivingScore = (donorsearchAiScores = [], dpsScore = null) => {
  const scoredRecords = donorsearchAiScores.filter((record) => record.score_value != null);

  for (const type of ['DSAI', 'ECR', 'MLR']) {
    const best = mostRecentRecord(scoredRecords.filter((record) => record.type === type));
    if (best) return { type, value: roundScore(best.score_value) };
  }

  return { type: 'DPS', value: roundScore(dpsScore) };
};

/**
 * Transforms DonorSearch data into component-ready props using single-source routing.
 * When summaryCounts exists (API path): all amounts and counts come from the /summary API.
 * When summaryCounts is null (table path): all amounts and counts come from the GQL enrichment table.
 *
 * Capacity/rating fields always come from the GQL enrichment table regardless of path.
 *
 * @param {Object} gqlData - Flat snake_case fields from the GQL query
 * @param {Object|null} summaryCounts - Parsed /summary API data (from transformDonorSearchSummary), or null for table-only path
 */
export const transformDonorSearchData = (gqlData = {}, summaryCounts = null) => {
  const {
    ds_rating,
    estimated_capacity,
    largest_gift_upper_estimate,
    total_gift_amount,
    total_gift_count,
    political_giving_amount,
    political_giving_count,
    real_estate_value,
    real_estate_count,
    sec_stock_value,
    updated_at,
  } = gqlData;

  // Capacity and rating fields always come from enrichment table
  const base = {
    dsRating: ds_rating || null,
    capacityWealth: formatCapacityRange(estimated_capacity),
    capacityGiving: formatCurrency(largest_gift_upper_estimate),
    updatedAt: updated_at,
    formattedUpdateDate: formatDate(updated_at),
  };

  // API path: all amounts and counts from /summary API exclusively
  if (summaryCounts) {
    return {
      ...base,
      charitableGiving: summaryCounts.charitableGiving,
      politicalGiving: summaryCounts.politicalGiving,
      crowdfunding: summaryCounts.crowdfunding,
      realEstate: summaryCounts.realEstate,
      secHoldings: summaryCounts.secHoldings,
    };
  }

  // Table path: all amounts and counts from GQL enrichment table only
  return {
    ...base,
    charitableGiving: {
      amount: total_gift_amount,
      highCount: total_gift_count,
      lowCount: 0,
    },
    politicalGiving: {
      amount: political_giving_amount,
      highCount: political_giving_count,
      lowCount: 0,
    },
    crowdfunding: null,
    realEstate: {
      amount: real_estate_value,
      highCount: real_estate_count,
      lowCount: 0,
    },
    secHoldings: {
      amount: sec_stock_value,
      highCount: 0,
      lowCount: 0,
    },
  };
};
